import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { LogIn, LogOut, Shield, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

export const LoginDialog = ({ open, onOpenChange, onSuccess }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { if (open) { setPassword(""); setError(""); } }, [open]);

  const submit = async () => {
    if (!email.trim() || !password) return;
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/login", { email: email.trim(), password });
      localStorage.setItem("access_token", data.access_token);
      localStorage.setItem("user", JSON.stringify(data.user));
      toast.success(`Welcome, ${data.user.email}`);
      onSuccess?.(data.user);
      onOpenChange(false);
    } catch (e) {
      const msg = formatApiErrorDetail(e?.response?.data?.detail) || e.message;
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111D38] border-slate-700 text-slate-100 max-w-md" data-testid="login-dialog">
        <DialogHeader>
          <DialogTitle className="text-slate-100 text-xl flex items-center gap-2">
            <Shield className="h-5 w-5 text-amber-400" /> Owner Login
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Sign in with your email and password to enable editing, importing and settings. Viewing stays open to everyone.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <Input
                type="email"
                autoFocus
                data-testid="input-login-email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-[#0B132B] border-slate-700 h-10 pl-9"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Password</Label>
            <Input
              type="password"
              data-testid="input-login-password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              className="bg-[#0B132B] border-slate-700 h-10 font-mono"
            />
          </div>
          {error && <p data-testid="login-error" className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-md px-3 py-2">{error}</p>}
          <p className="text-[11px] text-slate-500">Session kept 12 hours in this browser. Writes are protected; reads remain public.</p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-300">Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy || !email.trim() || !password}
            data-testid="btn-login-submit"
            className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold gap-1.5"
          >
            <LogIn className="h-4 w-4" /> {busy ? "Signing in..." : "Sign in"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const SessionBadge = ({ onLoginClick }) => {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("user") || "null"); } catch { return null; }
  });

  useEffect(() => {
    const sync = () => {
      try { setUser(JSON.parse(localStorage.getItem("user") || "null")); } catch { setUser(null); }
    };
    window.addEventListener("storage", sync);
    const i = setInterval(sync, 1500);
    return () => { window.removeEventListener("storage", sync); clearInterval(i); };
  }, []);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("access_token");
    localStorage.removeItem("user");
    setUser(null);
    toast.success("Signed out — view-only mode");
  };

  if (user) {
    return (
      <button
        onClick={logout}
        data-testid="btn-session-logout"
        className="h-9 px-3 rounded-lg flex items-center gap-1.5 bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 text-xs font-semibold hover:bg-emerald-500/20 transition max-w-[220px]"
        title={`Signed in as ${user.email} — click to sign out`}
      >
        <Shield className="h-3.5 w-3.5 flex-shrink-0" />
        <span className="truncate">{user.email}</span>
        <LogOut className="h-3 w-3 flex-shrink-0" />
      </button>
    );
  }
  return (
    <button
      onClick={onLoginClick}
      data-testid="btn-session-login"
      className="h-9 px-3 rounded-lg flex items-center gap-1.5 bg-slate-800/60 border border-slate-700/70 text-slate-300 text-xs font-semibold hover:bg-slate-700 transition"
      title="Sign in to edit"
    >
      <LogIn className="h-3.5 w-3.5" /> Sign in
    </button>
  );
};
